import { createRenderer } from './engine/renderer'
import { createCamera } from './engine/camera'
import { createInput } from './engine/input'
import { initAudio, playSfx } from './engine/audio'
import { initAmbientAudio } from './engine/ambient-audio'
import { initMusic } from './engine/music'
import { MUSIC_TRACKS } from './game/music-tracks'
import { loadNarratorAudio } from './engine/narrator'
import { loadMotherAudio } from './engine/mother-audio'
import { createWorld, seedDemo, type WorldCtx } from './game/world'
import { generateWorldRegion } from './game/dungeon'
import { setActiveDungeon } from './game/dungeon/active'
import { applyTier, currentTier, canEvolve } from './game/evolution'
import { applyPlayerRadius } from './game/growth'
import { applyFormEyes } from './game/systems/eyes'
import { spawnInkSplatter, emitRipple } from './game/systems/particles'
import { rollItem } from './game/items'
import { spawnItemOrb } from './game/systems/item-orb'
import { createGiftChooser } from './ui/gift'
import { loadProgress, saveProgress, snapshotProgress } from './game/persistence'
import { createEventBus } from './game/events'
import { createTimeScale } from './game/time-scale'
import { runStages, GAMEPLAY_STAGES, SPAWN_STAGES, CAMERA_STAGES, type FrameCtx } from './game/scheduler'
import { CONTROLS } from './game/controls'
import { createUiHotkeys } from './ui/ui-hotkeys'
import { createFocusNav } from './ui/focus-nav'
import { T } from './game/tunables'
import { buildPipeline } from './game/pipeline'
import { createLifecycle } from './game/lifecycle'
import { installDevTools, type DevFlags } from './game/dev/cheat'
import { createRenderSystem } from './game/systems/render'
import { onShopClose } from './game/systems/npc'
import { createHomePanel } from './ui/skill-shop'
import { createStatsCard } from './ui/stats-card'
import { createPickupCard } from './ui/pickup-card'
import { createCreatureGallery } from './ui/creature-gallery'
import { createLegend } from './ui/legend'
import { updateSkillNotice, showNotice } from './ui/skill-notice'
import { createMotherDialog } from './ui/mother-dialog'
import { restoreAttunement, attunedCheckpointId } from './game/systems/checkpoint'
import { createOrbBeacon } from './ui/orb-beacon'
import { createMutationTray } from './ui/mutation-tray'
import { createActionBar } from './ui/action-bar'
import { createGrowthSpurtChooser } from './ui/growth-spurt'
import { createHud } from './ui/hud'
import { createFloatNumbers } from './ui/float-numbers'

async function main() {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement | null
  const nogpu = document.getElementById('nogpu') as HTMLDivElement | null
  const skillNoticeEl = document.getElementById('skill-notice') as HTMLDivElement
  if (!canvas || !nogpu) throw new Error('missing #canvas or #nogpu')

  if (!navigator.gpu) {
    nogpu.style.display = 'flex'
    return
  }

  initAudio()
  void initAmbientAudio() // resolves after first user gesture unlocks audio
  void initMusic(MUSIC_TRACKS)
  void loadNarratorAudio()
  void loadMotherAudio()
  const renderer = await createRenderer(canvas)
  const camera = createCamera()
  // Dev: expose the camera so CDP playtests (amoeba-playtest Mode 1) can
  // worldToScreen arbitrary points and clip screenshots around them.
  if (import.meta.env.DEV) (window as unknown as { __camera: typeof camera }).__camera = camera
  const input = createInput(canvas, camera, CONTROLS, T.controls.pad)
  const world = createWorld()

  // Persisted progress loads BEFORE seeding: the world layout seed lives in
  // the save (B2 — the web is persistent per save, learnable like a real
  // place). A fresh save rolls a seed here and persists it on first save.
  const persisted = await loadProgress()
  let worldSeed = persisted?.world?.seed ?? ((Math.random() * 0xffffffff) >>> 0)
  const worldDoors = persisted?.world?.doors ?? {}
  const worldLayout = generateWorldRegion(worldSeed)
  // Re-open the shortcut doors this save already earned (B3).
  for (const door of worldLayout.doors ?? []) {
    if (worldDoors[`e${door.edge}`]) door.open = true
  }
  setActiveDungeon(worldLayout)
  const worldCtx: WorldCtx = { seed: worldSeed, layout: worldLayout }

  // Attunement restores BEFORE seeding so spawned daughter cells carry the
  // correct attuned flag from frame one (monorail-s8k).
  restoreAttunement(persisted?.checkpoint ?? null)

  const playerRef = { current: seedDemo(world, worldCtx) }

  // Restore persisted progress (tier + story flags) — seedDemo() already
  // created the player entity.
  const flagsState = { motherGift: false }
  if (persisted) {
    flagsState.motherGift = persisted.flags?.motherGift ?? false
    if (persisted.evolution.tier > 1) {
      applyTier(playerRef.current, persisted.evolution.tier, { animate: false })
      applyPlayerRadius(playerRef.current)
    }
    // Restore the divergent-evolution critter so a returning tier-3+ player
    // keeps what it became (the resolver only re-runs on a live speciation).
    if (persisted.evolution.form && playerRef.current.lineage) {
      playerRef.current.lineage.form = persisted.evolution.form
      applyFormEyes(playerRef.current)
    }
    // Restore gear — the collection and what was worn (A2, monorail-yqv).
    if (persisted.gear) {
      playerRef.current.inventory = persisted.gear.inventory
      playerRef.current.equipped = { ...persisted.gear.equipped }
    }
    // Restore key items (B4) — traversal keys are permanent like gear.
    if (persisted.keys) playerRef.current.keys = [...persisted.keys]
  }
  const persist = (): void => {
    const p = playerRef.current
    void saveProgress(snapshotProgress(
      currentTier(p), flagsState, p.lineage?.form,
      { inventory: p.inventory ?? [], equipped: { ...(p.equipped ?? {}) } },
      { seed: worldSeed, doors: worldDoors },
      p.keys,
      attunedCheckpointId() ?? undefined,
    ))
  }
  // First boot of a save: pin the freshly-rolled world seed immediately so
  // an early crash can't reroll the layout out from under the player.
  if (!persisted?.world) persist()

  const hud = createHud()
  const floatNumbers = createFloatNumbers()
  const mother = createMotherDialog()
  const orbBeacon = createOrbBeacon()
  const mutationTray = createMutationTray()
  const actionBar = createActionBar()
  const shop = createHomePanel(() => onShopClose(world), persist)
  const statsCard = createStatsCard(persist)
  const pickupCard = createPickupCard(persist)
  const legend = createLegend()
  const renderSystem = await createRenderSystem(renderer, world, camera, () => playerRef.current)
  const gallery = createCreatureGallery(renderSystem)

  const events = createEventBus()
  const spurtChooser = createGrowthSpurtChooser(events)
  const giftChooser = createGiftChooser(() => {
    flagsState.motherGift = true
    persist()
  })
  const pipeline = buildPipeline(events, {
    npcCallbacks: {
      // A live beat counts as "shop open" so the contact branch can't stack a
      // second beat on top of the first in the same swim-in.
      isShopOpen: () => shop.isOpen() || giftChooser.isOpen() || mother.isBeatActive(),
      greet: (_npc, line) => mother.greet(line),
      beat: (_npc, pages, onDone) => mother.beat(pages, onDone),
      hideDialog: () => mother.hide(),
      openShop: (player) => { shop.refresh(player); shop.open() },
      giftPending: () => !flagsState.motherGift,
      openGift: (player) => giftChooser.open(player),
    },
    // Item collected: it's already in the inventory — persist that, then the
    // compare card freezes the world for the wear-or-pocket choice. Gate and
    // treasure loot additionally burn their once-per-save flag (B4/B6).
    onItemPickup: (item, claimKey) => {
      if (claimKey !== undefined) worldDoors[claimKey] = true
      persist()
      pickupCard.show(playerRef.current, item)
    },
    // A shortcut earned stays earned — record + persist immediately.
    onDoorOpened: door => {
      worldDoors[`e${door.edge}`] = true
      persist()
    },
  })

  const timeScale = createTimeScale()

  // Frame glue subscribers — visual/audio/time reactions to gameplay facts.
  events.subscribe('damage.taken', e => {
    if (e.entity === playerRef.current) {
      hud.flashVignette()
      floatNumbers.taken(e.entity.position.x, e.entity.position.y, e.amount)
      // Haptics garnish (Phase F, amoeba-input plan) — no-ops without an
      // active pad or vibrationActuator support.
      input.rumble(120, 0.4, 0.7)
    }
  })
  // Numbers for strength/reward legibility (monorail-r9y).
  events.subscribe('damage.dealt', e => {
    if (e.target.position) floatNumbers.dealt(e.target, e.target.position.x, e.target.position.y, e.amount)
  })
  events.subscribe('creature.eaten', e => {
    if (e.eater === playerRef.current && e.gained > 0) {
      const p = playerRef.current
      floatNumbers.gain(p.position.x, p.position.y, e.gained)
      hud.popDna(e.gained)
    }
  })
  events.subscribe('boss.killed', () => camera.shake(0.08, 1.0))
  // Daughter-cell attunement (monorail-s8k): persist immediately (respawn
  // never re-persists), and the Mother speaks through her planted bud — the
  // ambient greet is the sole acknowledgment channel (no narrator line).
  const attuneLines = [
    'Rest here. It counts as visiting.',
    'If you come apart, come back here.',
    'I left a piece of myself out here. In case.',
  ]
  let attuneLineIndex = 0
  events.subscribe('checkpoint.attuned', () => {
    persist()
    mother.greet(attuneLines[attuneLineIndex++ % attuneLines.length])
  })
  // Persist tier + the divergent-evolution form the moment a morph completes —
  // speciation locks at the tier-3 BURST, so by completion the form is set
  // and this persist() captures it (after the shop's own persist()).
  events.subscribe('evolution.completed', () => persist())
  // Reversal bite — a predator becoming food gets a shallow hitstop dip so
  // the power shift registers as a beat, not just another eat.
  events.subscribe('predator.eaten', () => {
    playSfx('eat', { volume: 0.35 })
    timeScale.pulse(T.feel.reversalEat.slowmoScale, T.feel.reversalEat.slowmoHoldSec, T.feel.reversalEat.slowmoRecoverSec)
  })
  events.subscribe('boss.spawned', () => camera.shake(0.04, 0.6))
  // Portal trips (B5): snap the camera to the arrival point — a cross-world
  // lerp would smear the whole web past the eyepiece — plus a settle pulse.
  const onPortalTrip = (e: { x: number; y: number }): void => {
    camera.x = e.x
    camera.y = e.y
    camera.zoomPulse(T.feel.evolve.settleZoomMult, 0.6)
  }
  events.subscribe('portal.cast', onPortalTrip)
  events.subscribe('portal.return', onPortalTrip)
  // Gate-vault payoff (B4): a sealed pocket must pay out the moment it
  // opens — a guaranteed rare-or-better drop at the pocket's heart, once
  // per save (the loot flag rides world.doors).
  const spawnGateLoot = (edge: number): void => {
    const e = worldLayout.graph.edges[edge]
    const pocketId = worldLayout.graph.nodes[e.b].role === 'reward' ? e.b : e.a
    const p = worldLayout.pos[pocketId]
    const slot = Math.random() < 0.5 ? 'hat' : 'weapon'
    const rarity = Math.random() < 0.25 ? 'pristine' : 'rare'
    spawnItemOrb(world, p.x, p.y, rollItem(slot, { rarity }), `loot:e${edge}`)
  }
  // Treasure pockets (B6): ungated dead-ends whose guaranteed drop sits at
  // the pocket's heart from boot — the orb's twinkle IS the doorway
  // telegraph. Once per save; shallow pockets roll naturally, deep ones
  // floor at rare (the detour gets pricier, so does the promise).
  const spawnTreasureLoot = (nodeId: number): void => {
    const p = worldLayout.pos[nodeId]
    const slot = Math.random() < 0.5 ? 'hat' : 'weapon'
    const deep = (worldLayout.graph.nodes[nodeId].band ?? 0) >= 3
    let item = rollItem(slot)
    if (deep && item.rarity === 'uncommon') item = rollItem(slot, { rarity: 'rare', base: item.base })
    spawnItemOrb(world, p.x, p.y, item, `loot:n${nodeId}`)
  }
  // Door dissolving open (B3/B4) — a soft geographic beat: the membrane's
  // ripple, a wet pop, a breath of camera. The reward IS the path (and,
  // for gates, the vault behind it).
  events.subscribe('door.opened', e => {
    emitRipple(e.x, e.y, { strength: 0.4, lifetime: 0.9 })
    playSfx('eat', { volume: 0.5 })
    camera.zoomPulse(T.feel.evolve.leanInZoomMult, 0.7)
    if (e.kind !== 'shortcut') spawnGateLoot(e.edge)
  })
  // Unclaimed pocket loot re-offers on boot and on every respawn (death
  // mid-detour must not void the prize; respawn clears the world). Gates
  // only pay after opening; treasure pockets pay from the start.
  const reofferPocketLoot = (): void => {
    for (const door of worldLayout.doors ?? []) {
      if (door.open && door.kind !== 'shortcut' && !worldDoors[`loot:e${door.edge}`]) {
        spawnGateLoot(door.edge)
      }
    }
    for (const node of worldLayout.graph.nodes) {
      if (node.role === 'treasure' && !worldDoors[`loot:n${node.id}`]) {
        spawnTreasureLoot(node.id)
      }
    }
  }
  reofferPocketLoot()
  // The boss carries the paperwork (B4): first kill grants the hall pass.
  events.subscribe('boss.killed', () => {
    const pl = playerRef.current
    if ((pl.keys ?? []).includes('hall-pass')) return
    pl.keys = [...(pl.keys ?? []), 'hall-pass']
    events.emit({ type: 'key.granted', keyId: 'hall-pass' })
    persist()
  })
  events.subscribe('orb.collected', e => {
    if (e.kind !== 'dna') return
    playSfx('orb_collect', { volume: 0.22 })
    hud.flashOrbPickup()
    const p = playerRef.current
    floatNumbers.gain(p.position.x, p.position.y, e.value)
    hud.popDna(e.value)
  })
  // Evolution fanfare — the game's biggest moment, stacked across channels:
  // gather (inhale sound, observer leans in, eyes shut in evolutionSystem) →
  // burst (chord + thump, slowmo dip, soft shake) → settle (sit back, the
  // new body reads bigger). Magnitudes live in T.feel.evolve.
  events.subscribe('evolution.started', () => {
    playSfx('evolve_inhale', { volume: 0.5 })
    camera.zoomPulse(T.feel.evolve.leanInZoomMult, T.feel.evolve.leanInZoomSec)
  })
  events.subscribe('evolution.burst', () => {
    playSfx('evolve_burst', { volume: 0.6 })
    timeScale.pulse(T.feel.evolve.slowmoScale, T.feel.evolve.slowmoHoldSec, T.feel.evolve.slowmoRecoverSec)
    camera.shake(T.feel.evolve.burstShakeIntensity, T.feel.evolve.burstShakeDuration)
    camera.zoomPulse(T.feel.evolve.settleZoomMult, T.feel.evolve.settleZoomSec)
  })
  // Speciation — the tier-3 burst where the critter emerges. Stacks ON TOP of
  // the regular burst channels: ink splatter + ripple at the body (the burst
  // has no ripple normally — reserved for this), a bright arpeggio over the
  // chord, and a touch more camera. The narrator reveal line rides the same
  // event (systems/narrator.ts).
  events.subscribe('lineage.speciated', e => {
    const p = e.player.position
    spawnInkSplatter(world, p.x, p.y)
    emitRipple(p.x, p.y, { strength: 0.5, lifetime: 1.0 })
    playSfx('speciate_shimmer', { volume: 0.55 })
    camera.zoomPulse(T.feel.evolve.leanInZoomMult * 1.03, 1.2)
  })

  // "Worth a trip home?" — the only purchase is Anagenesis now.
  const canBuySomething = (p: typeof playerRef.current): boolean =>
    canEvolve(p, p.dna ?? 0).ok

  let last = performance.now()
  let elapsed = 0

  const lifecycle = createLifecycle({
    world, camera, playerRef, worldCtx,
    deathOverlay: document.getElementById('death-overlay'),
    onRespawned: () => {
      elapsed = 0
      last = performance.now()
      timeScale.reset()
      // A greet/beat can go live on the death frame; without this it would
      // straddle the overlay and leave the narrator gate latched shut.
      mother.hide()
      reofferPocketLoot()
      requestAnimationFrame(frame)
    },
  })

  const devFlags: DevFlags = import.meta.env.DEV
    ? installDevTools({
        playerRef, persist, world,
        worldLayout,
        rerollWorld: (seed?: number) => {
          worldSeed = seed ?? ((Math.random() * 0xffffffff) >>> 0)
          persist()
          location.reload()
        },
      })
    : { showDepth: false }

  // System-UI hotkeys live in the action table (ui/ui-hotkeys.ts). The
  // gallery keeps a slim raw listener: Backquote isn't a gameplay verb, and
  // its T-to-cycle only applies while open (NOT dev-gated — the gallery
  // ships; only the deleted M scheme-cycler was dev-only).
  window.addEventListener('keydown', e => {
    if (e.code === 'Backquote') gallery.toggle(world, camera)
    if (e.code === 'KeyT' && gallery.isOpen()) gallery.cycleTier()
  })
  const uiHotkeys = createUiHotkeys({ world, playerRef, shop, statsCard, legend, orbBeacon })

  // Focus nav (Phase E, amoeba-input plan) drives the four in-run choice
  // panels with keyboard/d-pad: real DOM focus + a real .click(), since
  // synthetic KeyboardEvents don't fire the panels' own listeners. Only the
  // pickup card gets onCancel (Q/Esc pockets it) — the shop's cancel already
  // lives in ui-hotkeys above, and the spurt/gift choosers are mandatory.
  const focusNav = createFocusNav()
  focusNav.register({
    isOpen: () => pickupCard.isOpen(),
    root: () => document.getElementById('pickup-card') as HTMLElement,
    onCancel: () => pickupCard.choose(false),
  })
  focusNav.register({ isOpen: () => shop.isOpen(), root: () => document.getElementById('skill-shop') as HTMLElement })
  focusNav.register({ isOpen: () => spurtChooser.isOpen(), root: () => document.getElementById('growth-spurt') as HTMLElement })
  focusNav.register({ isOpen: () => giftChooser.isOpen(), root: () => document.getElementById('gift-chooser') as HTMLElement })

  // Device-aware UI glyphs + controller onboarding (Phase F, amoeba-input
  // plan). lastInputDevice starts 'kbm' to match the labels already baked
  // into index.html/action-bar's initial render, so no glyph swap fires
  // until an actual flip happens. The two toasts are session-once.
  let lastInputDevice: 'kbm' | 'pad' = 'kbm'
  let padToastShown = false
  let padUnknownToastShown = false

  const frame = (now: number) => {
    const rawDt = Math.min((now - last) / 1000, 1 / 30)
    last = now
    // Global time-scale (hitstop/slowmo). Ticks on real time; simulation and
    // shader clocks below run on the scaled dt.
    timeScale.tick(rawDt)
    const dt = rawDt * timeScale.value
    input.tick(now)

    // Any freeze panel (incl. stats/gallery) flips actions into 'menu'
    // context: gameplay verbs are suppressed and the context switch drops
    // buffered presses, so an Enter inside a menu can't fire an attack on
    // close. UI hotkeys run BEFORE the freeze gates so panels stay closable.
    const anyPanelOpen = gallery.isOpen() || shop.isOpen() || statsCard.isOpen()
      || pickupCard.isOpen() || spurtChooser.isOpen() || giftChooser.isOpen()
      || mother.isBeatActive()
    input.actions.setContext(anyPanelOpen ? 'menu' : 'gameplay')
    uiHotkeys.tick(input.actions)
    focusNav.tick(input.actions)

    // Device-aware UI glyphs (Phase F) — cheap: one comparison a frame, only
    // touches the DOM on an actual flip between keyboard/mouse and pad.
    if (input.actions.lastDevice !== lastInputDevice) {
      lastInputDevice = input.actions.lastDevice
      actionBar.setDevice(lastInputDevice)
      legend.setDevice(lastInputDevice)
      pickupCard.setDevice(lastInputDevice)
      mother.setDevice(lastInputDevice)
    }
    // Controller onboarding toasts (Phase F) — pads are invisible until the
    // player actually touches one; each fires at most once per session.
    if (!padToastShown && input.actions.lastDevice === 'pad') {
      padToastShown = true
      showNotice('controller detected — A dash · X attack · RB interact')
    }
    if (!padUnknownToastShown && input.pad.connected && !input.pad.recognized) {
      padUnknownToastShown = true
      showNotice('this controller speaks an unknown dialect — buttons ignored')
    }

    // Right-stick zoom, parity with wheel zoom: a per-frame read applied
    // before the freeze gates, on rawDt (NOT the time-scaled dt) — slowmo
    // must not slow zoom, and wheel zoom isn't time-scaled either.
    const zy = input.actions.vec('zoom').y
    if (zy !== 0) camera.zoom *= Math.exp(zy * T.controls.pad.zoomRate * rawDt)

    // Pad-disconnect auto-pause (genre convention): losing the active pad
    // opens the stats card, which freezes the sim — same as a console losing
    // its controller. Only fires if nothing else is already open.
    if (input.pad.justDisconnected && input.actions.lastDevice === 'pad' && !anyPanelOpen) {
      statsCard.toggle(playerRef.current)
    }

    // Gallery / shop gates — freeze simulation.
    if (gallery.isOpen()) {
      elapsed += dt
      gallery.tick(elapsed, camera)
      renderSystem.draw(elapsed)
      requestAnimationFrame(frame)
      return
    }
    if (shop.isOpen()) {
      renderSystem.draw(elapsed)
      requestAnimationFrame(frame)
      return
    }
    // Specimen report — same freeze contract as the shop.
    if (statsCard.isOpen()) {
      renderSystem.draw(elapsed)
      requestAnimationFrame(frame)
      return
    }
    // Pickup compare card — the drop reveal holds the world still.
    if (pickupCard.isOpen()) {
      renderSystem.draw(elapsed)
      requestAnimationFrame(frame)
      return
    }
    // Growth-spurt chooser — same freeze contract as the shop: the world
    // holds still while the player picks a mutation.
    if (spurtChooser.isOpen()) {
      renderSystem.draw(elapsed)
      requestAnimationFrame(frame)
      return
    }
    if (giftChooser.isOpen()) {
      renderSystem.draw(elapsed)
      requestAnimationFrame(frame)
      return
    }
    // Mother dialog beat — same freeze contract as the shop. The dialog is
    // the sole menuConfirm consumer while a beat is up (not focus-nav
    // registered: linear advance, no focusables). Ticks on real time.
    if (mother.isBeatActive()) {
      mother.tick(input.actions, rawDt * 1000)
      renderSystem.draw(elapsed)
      requestAnimationFrame(frame)
      return
    }

    elapsed += dt
    const ctx: FrameCtx = { world, dt, elapsed, now, input, camera, events, playerRef }

    runStages(ctx, pipeline, GAMEPLAY_STAGES)
    const dead = (playerRef.current.health?.current ?? 1) <= 0
    if (dead) {
      events.emit({ type: 'player.died' })
    } else {
      runStages(ctx, pipeline, SPAWN_STAGES)
      runStages(ctx, pipeline, CAMERA_STAGES)
    }
    // Single drain per simulated frame — death frames included, so no event
    // outlives the frame that emitted it.
    events.drain()

    if (dead) {
      lifecycle.showDeath()
      renderSystem.draw(elapsed)
      hud.update(playerRef.current, devFlags.showDepth)
      return // loop restarts via lifecycle.onRespawned
    }

    // A filled spurt meter opens the chooser; the freeze gate above takes
    // over on the next frame.
    spurtChooser.maybeOpen(playerRef.current)

    renderSystem.draw(elapsed)
    mother.tick(input.actions, rawDt * 1000)
    floatNumbers.tick(dt, camera, renderer.width, renderer.height)
    orbBeacon.tick(world, playerRef.current, camera, renderer.width, renderer.height, canBuySomething(playerRef.current))
    mutationTray.tick(playerRef.current)
    actionBar.tick(playerRef.current)
    hud.update(playerRef.current, devFlags.showDepth)
    updateSkillNotice(skillNoticeEl, playerRef.current, shop.isOpen())

    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)
}

main().catch(err => {
  console.error(err)
  const nogpu = document.getElementById('nogpu')
  if (nogpu) {
    nogpu.style.display = 'flex'
    nogpu.innerHTML = `<p>amoeba failed to start:<br><code>${String(err).replace(/</g, '&lt;')}</code></p>`
  }
})
